import React, { useEffect, useRef, useState } from 'react';
import {
  USER_ACTION_FEEDBACK_EVENT,
  type UserActionFeedbackDetail,
} from '../services/UserActionFeedback';

const STATUS_STYLE: Record<UserActionFeedbackDetail['status'], { icon: string; iconClass: string; border: string }> = {
  working: { icon: 'fa-circle-notch fa-spin', iconClass: 'text-[#0f8b8d]', border: 'border-[#0f8b8d]/30' },
  success: { icon: 'fa-circle-check', iconClass: 'text-[#167452]', border: 'border-[#167452]/30' },
  error: { icon: 'fa-circle-exclamation', iconClass: 'text-[#b33f51]', border: 'border-[#b33f51]/30' },
  info: { icon: 'fa-circle-info', iconClass: 'text-[#946012]', border: 'border-[#946012]/30' },
};
export const UserActionFeedbackCenter: React.FC = () => {
  const [items, setItems] = useState<UserActionFeedbackDetail[]>([]);
  const timers = useRef(new Map<string, number>());

  useEffect(() => {
    const remove = (id: string) => {
      setItems(current => current.filter(item => item.id !== id));
      const timer = timers.current.get(id);
      if (timer) window.clearTimeout(timer);
      timers.current.delete(id);
    };

    const handleFeedback = (event: Event) => {
      const detail = (event as CustomEvent<UserActionFeedbackDetail>).detail;
      if (!detail?.id || !detail.title) return;
      setItems(current => {
        const next = current.some(item => item.id === detail.id)
          ? current.map(item => item.id === detail.id ? detail : item)
          : [...current, detail];
        return next.slice(-3);
      });

      const previousTimer = timers.current.get(detail.id);
      if (previousTimer) window.clearTimeout(previousTimer);
      timers.current.delete(detail.id);
      const dismissAfter = detail.dismissAfterMs
        ?? (detail.status === 'working' ? 0 : detail.status === 'error' ? 8_000 : 5_000);
      if (dismissAfter > 0) {
        timers.current.set(detail.id, window.setTimeout(() => remove(detail.id), dismissAfter));
      }
    };

    window.addEventListener(USER_ACTION_FEEDBACK_EVENT, handleFeedback);
    return () => {
      window.removeEventListener(USER_ACTION_FEEDBACK_EVENT, handleFeedback);
      timers.current.forEach(timer => window.clearTimeout(timer));
      timers.current.clear();
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-24 right-4 z-[10000] flex w-[min(390px,calc(100vw-2rem))] flex-col gap-2 md:bottom-5"
      aria-live="polite"
      aria-atomic="false"
      data-testid="user-action-feedback-center"
    >
      {items.map(item => {
        const style = STATUS_STYLE[item.status];
        return (
          <div
            key={item.id}
            role={item.status === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-3 rounded-2xl border ${style.border} bg-white/95 px-4 py-3 shadow-[0_18px_45px_rgba(15,35,45,0.18)] backdrop-blur-xl`}
          >
            <i className={`fa-solid ${style.icon} ${style.iconClass} mt-0.5 text-base`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#171a20]">{item.title}</p>
              {item.detail && <p className="mt-1 break-words text-[11px] leading-relaxed text-[#5f676f]">{item.detail}</p>}
            </div>
            <button
              type="button"
              onClick={() => setItems(current => current.filter(entry => entry.id !== item.id))}
              aria-label={`${item.title} 알림 닫기`}
              className="rounded-lg p-1 text-[#8a9097] transition hover:bg-[#edf0f2] hover:text-[#171a20]"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
